import {
  CheckboxIcon,
  CheckCircledIcon,
  EnvelopeClosedIcon,
  FileTextIcon,
  Pencil2Icon,
  PersonIcon,
  ViewVerticalIcon,
} from "@radix-ui/react-icons";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { formatDate, formatDateTime } from "components/Forms/ResponseDialog";
import { namesInWords } from "components/MessageTemplates/TemplatePreviewParts";
import { Fragment, ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import ActivityTabChange, {
  ChangeDraft,
  draftOf,
  editsOf,
} from "./ActivityTabChange";
import {
  aboutOf,
  approverWords,
  askedWho,
  AutomationLink,
  PeoplePills,
  proposalTitle,
  useAutomationIndex,
} from "./ActivityTabParts";
import { useReference } from "./hooks";
import { statusLabel } from "./kinds";
import { columnById } from "./reference";
import type { ApprovalAnswer, ApprovalItem, StepAction } from "./types";
import { ACTION_LABELS, capitalize, channelWords, COPY } from "./words";

const ICONS: Record<StepAction, ReactNode> = {
  task: <CheckboxIcon />,
  message: <EnvelopeClosedIcon />,
  crm_move: <ViewVerticalIcon />,
  crm_field: <Pencil2Icon />,
  close_task: <CheckCircledIcon />,
  assign_tutor: <PersonIcon />,
  add_note: <FileTextIcon />,
};

/** The yes button: Add a task, Send a message, Move them, Update a field, Close a task, Assign a tutor, Add a note. */
export const ANSWER_WORDS: Record<StepAction, string> = {
  task: "Add",
  message: "Send",
  crm_move: "Move",
  crm_field: "Update",
  close_task: "Close",
  assign_tutor: "Assign",
  add_note: "Add",
};

/** What the card would do, beyond its title: a task's due date and people, a message's people and channels, a move's board. */
function Details({ item }: { item: ApprovalItem }) {
  const ref = useReference();
  const p = item.proposal;
  const rows: [string, ReactNode][] = [];
  if (p.action === "task") {
    rows.push(["Due", formatDate(p.dueDate)]);
    rows.push([
      "Assigned to",
      <>
        <PeoplePills people={p.assignees} />
        {p.fallback && (
          <span className="mt-1 block text-xs text-gray-500">
            {COPY.fallbackNote(ref)}
          </span>
        )}
      </>,
    ]);
    if (p.description.trim())
      rows.push([
        "Description",
        <span className="whitespace-pre-wrap">{p.description}</span>,
      ]);
  }
  if (p.action === "message") {
    rows.push(["To", <PeoplePills people={p.to} />]);
    rows.push(["By", capitalize(channelWords(p).replace(/^by /, ""))]);
  }
  if (p.action === "crm_move")
    rows.push([
      "Board",
      columnById(ref, p.boardId, p.columnId)?.board.name ?? p.columnLabel,
    ]);
  if (p.action === "close_task") {
    if (p.tasks.length)
      rows.push([
        p.tasks.length > 1 ? "Tasks" : "Task",
        namesInWords(p.tasks.map((t) => t.name ?? "A task that was deleted")),
      ]);
    rows.push(["Mark as", statusLabel(p.status)]);
  }
  if (p.action === "assign_tutor" && p.tutors.length)
    rows.push([
      p.tutors.length > 1 ? "Tutors" : "Tutor",
      <PeoplePills people={p.tutors} />,
    ]);
  if (p.action === "add_note" && p.text)
    rows.push(["Note", <span className="whitespace-pre-wrap">{p.text}</span>]);
  if (!rows.length) return null;
  return (
    <dl className="mb-0 mt-4 grid grid-cols-[6.5rem,1fr] gap-x-4 gap-y-2 rounded-lg bg-gray-50 px-4 py-3 text-sm">
      {rows.map(([label, value]) => (
        <Fragment key={label}>
          <dt className="font-normal text-gray-500">{label}</dt>
          <dd className="m-0 min-w-0 text-gray-900">{value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

/**
 * One step waiting for approval: what it would do, who was asked and
 * when, who it is about, the automation it came from and who can
 * approve. Add (Send, Move, Update) and Not now answer it in one click.
 * A task or CRM card's Change it first opens its fields, filled in, and
 * Add then does it with the changes; a message's wording is its
 * template's, so its card links there instead.
 */
export default function ActivityTabCard({
  item,
  automations,
  busy,
  onAnswer,
}: {
  item: ApprovalItem;
  automations: ReturnType<typeof useAutomationIndex>;
  /** Its answer is on its way. */
  busy: boolean;
  onAnswer: (answer: ApprovalAnswer) => void;
}) {
  const ref = useReference();
  const [draft, setDraft] = useState<ChangeDraft | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const action = item.proposal.action;
  const title = proposalTitle(ref, item.proposal);
  const word = ANSWER_WORDS[action];
  const templateId =
    item.proposal.action === "message" ? item.proposal.templateId : null;

  const confirm = () => {
    if (!draft) {
      onAnswer({ decision: "approve" });
      return;
    }
    const checked = editsOf(ref, item, draft);
    if ("problem" in checked) {
      setProblem(checked.problem);
      return;
    }
    setProblem(null);
    onAnswer({ decision: "approve", edits: checked.edits });
  };
  const secondary = {
    color: ButtonColor.GREEN,
    fill: ButtonFill.HOLLOW,
    size: ButtonSize.SMALL,
    disabled: busy,
  };

  return (
    <article
      aria-label={`${ACTION_LABELS[action]}: ${title}`}
      className={`rounded-lg border border-gray-200 bg-white p-5 transition ${
        busy ? "opacity-50" : ""
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5 font-medium text-gray-600">
          <span className="text-gray-400" aria-hidden>
            {ICONS[action]}
          </span>
          {ACTION_LABELS[action]}
        </span>
        <span>
          Asked {askedWho(ref, item.askedOf)} on {formatDateTime(item.askedAt)}
        </span>
      </div>
      <h3 className="mt-3 text-base font-semibold text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-600">{item.reason}</p>
      {item.note && <p className="mt-1 text-sm text-amber-800">{item.note}</p>}

      {draft ? (
        <div className="mt-4">
          <ActivityTabChange
            item={item}
            draft={draft}
            onChange={setDraft}
            trigger={automations.automation(item.automationId)?.trigger ?? null}
          />
        </div>
      ) : (
        <Details item={item} />
      )}
      {problem && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {problem}
        </p>
      )}

      <div className="mt-5">
        <div className="flex flex-wrap gap-2">
          <Button
            color={ButtonColor.GREEN}
            size={ButtonSize.SMALL}
            disabled={busy}
            data-card-yes=""
            aria-label={`${word}: ${title}`}
            onClick={confirm}
          >
            {word}
          </Button>
          {draft ? (
            <Button
              {...secondary}
              onClick={() => {
                setDraft(null);
                setProblem(null);
              }}
            >
              Cancel
            </Button>
          ) : (
            <>
              {action !== "message" && (
                <Button
                  {...secondary}
                  aria-label={`Change it first: ${title}`}
                  onClick={() => setDraft(draftOf(ref, item))}
                >
                  Change it first
                </Button>
              )}
              <Button
                {...secondary}
                aria-label={`Not now: ${title}`}
                onClick={() => onAnswer({ decision: "dismiss" })}
              >
                Not now
              </Button>
              {templateId !== null && (
                <Link
                  to={`/message-templates/${templateId}`}
                  className="self-center text-sm font-semibold text-green-700 hover:underline"
                >
                  Edit template
                </Link>
              )}
            </>
          )}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-gray-100 pt-3 text-xs text-gray-500">
          <span className="inline-flex flex-wrap items-center gap-1.5">
            About <PeoplePills people={aboutOf(item)} />
          </span>
          <span>
            From{" "}
            <AutomationLink
              id={item.automationId}
              name={automations.name(item.automationId, item.automationName)}
              live={automations.live(item.automationId)}
            />
          </span>
          {/* Admins can approve every item, so only a wider choice is said. */}
          {item.approvers && item.approvers !== "admins" && (
            <span>{approverWords(item.approvers)}</span>
          )}
        </div>
      </div>
    </article>
  );
}
